import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  ArrowRightIcon,
  BoxIcon,
  CalendarDaysIcon,
  MapPinnedIcon,
  SearchIcon,
} from 'lucide-react';
import {
  type TwinCatalogItem,
  useListTwins,
} from '@cdf/api-client-react';
import { PublicFooter } from '@/components/layout/public-footer';
import { PublicHeader } from '@/components/layout/public-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type TwinFilter = 'all' | 'event' | 'location';

const PAGE_SIZE = 24;

const FILTERS: Array<{ value: TwinFilter; label: string }> = [
  { value: 'all', label: 'All twins' },
  { value: 'event', label: 'Events' },
  { value: 'location', label: 'Locations' },
];

function twinHref(twin: TwinCatalogItem): string {
  return twin.entityType === 'event'
    ? `/events/${twin.entitySlug}/twin`
    : `/twin/locations/${twin.entitySlug}`;
}

function statusLabel(status: string): string {
  return status.replace(/[-_]/g, ' ');
}

function TwinCard({ twin }: { twin: TwinCatalogItem }) {
  const isEvent = twin.entityType === 'event';
  const Icon = isEvent ? CalendarDaysIcon : MapPinnedIcon;

  return (
    <Card className="group h-full transition-shadow hover:shadow-md">
      <CardHeader className="gap-3 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div
            className={cn(
              'flex size-11 shrink-0 items-center justify-center rounded-lg',
              isEvent ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
            )}
          >
            <Icon className="size-5" aria-hidden />
          </div>
          <Badge variant={twin.scheduleStatus === 'available' ? 'default' : 'secondary'} className="capitalize">
            {statusLabel(twin.scheduleStatus)}
          </Badge>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {isEvent ? 'Event twin' : 'Location twin'}
          </p>
          <CardTitle className="text-lg">{twin.title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        <CardDescription className="flex-1 leading-relaxed">{twin.summary}</CardDescription>
        <Button asChild className="w-full">
          <Link to={twinHref(twin)}>
            Open 3D twin
            <ArrowRightIcon className="size-4" aria-hidden />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function TwinCardGrid({ twins, emptyMessage }: { twins: TwinCatalogItem[]; emptyMessage: string }) {
  if (twins.length === 0) {
    return (
      <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {twins.map((twin) => (
        <TwinCard key={twin.id} twin={twin} />
      ))}
    </div>
  );
}

export function TwinHubPage() {
  const [filter, setFilter] = useState<TwinFilter>('all');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const twinsQuery = useListTwins({ limit: PAGE_SIZE, offset });

  const visibleTwins = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    return (twinsQuery.data?.items ?? []).filter((twin) => {
      const matchesType = filter === 'all' || twin.entityType === filter;
      const matchesSearch =
        normalizedSearch.length === 0 ||
        `${twin.title} ${twin.summary}`.toLocaleLowerCase().includes(normalizedSearch);
      return matchesType && matchesSearch;
    });
  }, [filter, search, twinsQuery.data?.items]);

  const eventTwins = visibleTwins.filter((twin) => twin.entityType === 'event');
  const locationTwins = visibleTwins.filter((twin) => twin.entityType === 'location');
  const pagination = twinsQuery.data?.pagination;

  return (
    <div className="flex min-h-dvh flex-col">
      <PublicHeader />
      <main className="flex-1">
        <section className="border-b bg-muted/30">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
            <div className="max-w-3xl">
              <div className="mb-4 flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
                <BoxIcon className="size-6" aria-hidden />
              </div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">Digital Twin Hub</p>
              <h1 className="mt-3 text-4xl font-bold tracking-tight sm:text-5xl">Explore places and events in 3D</h1>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-muted-foreground">
                Open an interactive twin to explore a conference, property, or service area with the context that matters most.
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
          <div className="flex flex-col gap-4 rounded-xl border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-md">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search digital twins"
                className="pl-9"
                aria-label="Search digital twins"
              />
            </div>
            <div className="flex flex-wrap gap-2" aria-label="Filter digital twins">
              {FILTERS.map((option) => (
                <Button
                  key={option.value}
                  type="button"
                  size="sm"
                  variant={filter === option.value ? 'default' : 'outline'}
                  onClick={() => setFilter(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          {twinsQuery.isLoading ? (
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {['first', 'second', 'third'].map((id) => (
                <Skeleton key={id} className="h-64 rounded-xl" />
              ))}
            </div>
          ) : twinsQuery.isError ? (
            <div className="mt-8 rounded-xl border border-destructive/30 bg-destructive/5 p-6">
              <h2 className="font-semibold">Digital twins are unavailable</h2>
              <p className="mt-1 text-sm text-muted-foreground">Please try again in a moment.</p>
            </div>
          ) : (
            <div className="mt-10 space-y-12">
              {(filter === 'all' || filter === 'event') && (
                <section aria-labelledby="event-twins-heading">
                  <div className="mb-4 flex items-center gap-3">
                    <CalendarDaysIcon className="size-5 text-primary" aria-hidden />
                    <div>
                      <h2 id="event-twins-heading" className="text-2xl font-semibold">Event twins</h2>
                      <p className="text-sm text-muted-foreground">Explore active programs and venues.</p>
                    </div>
                  </div>
                  <TwinCardGrid twins={eventTwins} emptyMessage="No event twins match your search." />
                </section>
              )}

              {(filter === 'all' || filter === 'location') && (
                <section aria-labelledby="location-twins-heading">
                  <div className="mb-4 flex items-center gap-3">
                    <MapPinnedIcon className="size-5 text-primary" aria-hidden />
                    <div>
                      <h2 id="location-twins-heading" className="text-2xl font-semibold">Location twins</h2>
                      <p className="text-sm text-muted-foreground">Explore properties and local service areas.</p>
                    </div>
                  </div>
                  <TwinCardGrid twins={locationTwins} emptyMessage="No location twins match your search." />
                </section>
              )}

              {pagination && (pagination.offset > 0 || pagination.hasMore) && (
                <nav className="flex items-center justify-between gap-3 border-t pt-6" aria-label="Digital twin pages">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={pagination.offset === 0}
                    onClick={() => setOffset(Math.max(0, pagination.offset - PAGE_SIZE))}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">Page {Math.floor(pagination.offset / PAGE_SIZE) + 1}</span>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!pagination.hasMore}
                    onClick={() => setOffset(pagination.offset + PAGE_SIZE)}
                  >
                    Next
                  </Button>
                </nav>
              )}
            </div>
          )}
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
